import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import InputOtp from 'components/UI/InputOtp';
import Modal from 'components/Modal';
import Translate from 'components/Translate';
import { OTP_LENGTH } from 'constants/global';
import { SIGNUP_OTP_CHANNEL } from 'constants/registration';
import { GA_REGISTRATION_ACTIONS, GA_REGISTRATION_LABELS } from 'constants/gaEvents';
import SuccessModal from 'components/FeedbackModals/SuccessModal';
import { openSupport } from 'utils/openSupport';
import StepHeader from './StepHeader';

const MAX_TIME = 30 * 60 * 1000;
const RESEND_TIME = 40000;

const ClientOTPValidation = ({
  literals,
  user,
  onContinue,
  onResend,
  onBack,
  onEvent,
  onError,
  headerStep,
  headerTotal,
  channel = SIGNUP_OTP_CHANNEL.WHATSAPP,
  refreshingOtp = false,
  smsFallbackSent = false,
  otpReset = null,
  onCloseSmsFallback,
}) => {
  const [timer, setTimer] = useState({
    sms: { start: new Date().getTime(), left: MAX_TIME / 1000, resend: RESEND_TIME / 1000 },
    email: { start: new Date().getTime(), left: MAX_TIME / 1000, resend: RESEND_TIME / 1000 },
  });
  const [otp, setOtp] = useState({ email: '', phone: '' });
  const [inputGeneration, setInputGeneration] = useState({ phone: 0, email: 0 });
  const [confirmResend, setConfirmResend] = useState(null);
  const [successResend, setSuccessResend] = useState(false);
  const timerRef = useRef(null);

  const formatTime = (time) => {
    if (time <= 0) {
      return '00:00';
    }
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        const now = new Date().getTime();
        return {
          sms: {
            ...prev.sms,
            left: Math.floor((MAX_TIME - (now - prev.sms.start)) / 1000),
            resend: Math.floor((RESEND_TIME - (now - prev.sms.start)) / 1000),
          },
          email: {
            ...prev.email,
            left: Math.floor((MAX_TIME - (now - prev.email.start)) / 1000),
            resend: Math.floor((RESEND_TIME - (now - prev.email.start)) / 1000),
          },
        };
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    if (!otpReset?.field) {
      return;
    }
    const { field } = otpReset;
    setOtp((prev) => ({ ...prev, [field]: '' }));
    setInputGeneration((prev) => ({
      ...prev,
      [field]: prev[field] + 1,
    }));
  }, [otpReset]);

  useEffect(() => {
    if (channel !== SIGNUP_OTP_CHANNEL.SMS) {
      return;
    }
    setTimer((prev) => ({
      ...prev,
      sms: {
        start: new Date().getTime(),
        left: MAX_TIME / 1000,
        resend: RESEND_TIME / 1000,
      },
    }));
  }, [channel]);

  const handleResend = (type) => {
    if (confirmResend) {
      onEvent(
        GA_REGISTRATION_ACTIONS.EVENT,
        GA_REGISTRATION_LABELS.OTP_VALIDATION_RESEND,
      );
      onResend(type).then(() => {
        setSuccessResend(true);
        setTimer((prev) => ({
          ...prev,
          [type]: {
            ...prev[type],
            start: new Date().getTime(),
            left: MAX_TIME / 1000,
            resend: RESEND_TIME / 1000,
          },
        }));
      }).catch((e) => {
        if (e?.data?.error === 'ERROR_SENDING_WHATSAPP') {
          onError(e);
        } else {
          onError(literals.OTPValidation.errorResend);
        }
      }).finally(() => {
        setConfirmResend(null);
      });
    } else {
      setConfirmResend(type);
    }
  };

  const handleContinue = () => {
    onEvent(
      GA_REGISTRATION_ACTIONS.EVENT,
      GA_REGISTRATION_LABELS.OTP_VALIDATION_SUBMIT,
    );
    onContinue(otp);
  };

  const canContinue = otp.phone.length < OTP_LENGTH || otp.email.length < OTP_LENGTH;

  return (
    <div className='full-screen-container'>
      <div className='screen-content'>
        <StepHeader step={headerStep} total={headerTotal} onEvent={onEvent} />
        {refreshingOtp && (
          <p className='mb-4'>{literals.OTPValidation.refreshCode}</p>
        )}
        <p className='font-bold text-primary mb-4 text-xl'>
          {channel === SIGNUP_OTP_CHANNEL.WHATSAPP
            ? literals.OTPValidation.whatsappVerification
            : literals.OTPValidation.smsVerification}
        </p>
        <Translate
          className='mb-6'
          literal={channel === SIGNUP_OTP_CHANNEL.WHATSAPP
            ? literals.OTPValidation.whatsappVerificationDescription
            : literals.OTPValidation.smsVerificationDescription}
          vars={{ phone: user.phone }}
        />
        <p className='text-sm mb-6 underline' onClick={onBack}>{literals.OTPValidation.itsNotMyPhone}</p>
        <div className='flex justify-between'>
          <p className='font-black'>{literals.OTPValidation.codeExpirationTime}</p>
          <p className='font-bold text-primary'>{`${formatTime(timer.sms.left)} min`}</p>
        </div>
        <InputOtp
          key={`phone-${inputGeneration.phone}`}
          className='mb-4'
          value={otp.phone}
          onChange={(phone) => setOtp({ ...otp, phone })}
        />
        <p
          className='text-sm mb-6 underline'
          onClick={() => handleResend('sms')}
        >
          {literals.OTPValidation.notReceivedCode}
        </p>
        <p className='font-bold text-primary mb-4 text-xl'>{literals.OTPValidation.emailVerification}</p>
        <Translate className='mb-6' literal={literals.OTPValidation.emailVerificationDescription} vars={{ email: user.email }} />
        <p className='text-sm mb-6 underline' onClick={onBack}>{literals.OTPValidation.itsNotMyEmail}</p>
        <div className='flex justify-between'>
          <p className='font-black'>{literals.OTPValidation.codeExpirationTime}</p>
          <p className='font-bold text-primary'>{`${formatTime(timer.email.left)} min`}</p>
        </div>
        <InputOtp
          key={`email-${inputGeneration.email}`}
          className='mb-4'
          value={otp.email}
          onChange={(email) => setOtp({ ...otp, email })}
        />
        <p
          className='text-sm mb-6 underline'
          onClick={() => handleResend('email')}
        >
          {literals.OTPValidation.notReceivedCode}
        </p>
        {confirmResend && (
          <Modal title={literals.OTPValidation.resendCode} position='end' onClose={() => setConfirmResend(null)} animation='slide-up-enter'>
            <Translate
              className='mt-2'
              literal={timer[confirmResend].resend > 0 ? literals.OTPValidation.waitToSendCode : literals.OTPValidation.confirmResend}
              vars={{ time: timer[confirmResend].resend }}
            />
            <Button
              className='mt-2'
              text={literals.OTPValidation.resend}
              disabled={timer[confirmResend].resend > 0}
              onClick={() => handleResend(confirmResend)}
            />
            <Button
              className='mt-2'
              type='outline-primary'
              text={literals.common.cancel}
              onClick={() => setConfirmResend(null)}
            />
          </Modal>
        )}
        {(successResend || smsFallbackSent) && (
          <SuccessModal
            title={literals.OTPValidation.resendSuccess}
            description={smsFallbackSent
              ? literals.OTPValidation.smsFallbackSuccess
              : literals.OTPValidation.resendCodeSuccess}
            onClose={() => {
              setSuccessResend(false);
              if (onCloseSmsFallback) onCloseSmsFallback();
            }}
          >
            <Button
              className='mt-6'
              text={literals.common.close}
              onClick={() => {
                setSuccessResend(false);
                if (onCloseSmsFallback) onCloseSmsFallback();
              }}
            />
          </SuccessModal>
        )}
      </div>
      <div className='screen-actions'>
        <Button onClick={handleContinue} disabled={canContinue}>{literals.common.next}</Button>
        <Button type='outline-primary' onClick={openSupport}>{literals.contactSupport}</Button>
      </div>
    </div>
  );
};

ClientOTPValidation.propTypes = {
  literals: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  onContinue: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onEvent: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  headerStep: PropTypes.number.isRequired,
  headerTotal: PropTypes.number.isRequired,
  channel: PropTypes.string,
  refreshingOtp: PropTypes.bool,
  smsFallbackSent: PropTypes.bool,
  otpReset: PropTypes.shape({
    field: PropTypes.oneOf(['phone', 'email']),
    id: PropTypes.number,
  }),
  onCloseSmsFallback: PropTypes.func,
};

export default ClientOTPValidation;
